import { Box, Typography, Paper } from "@material-ui/core";
// material
import { styled, alpha } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import AuthFirebaseSocials from "../../components/authentication/AuthFirebaseSocial";
import EmailPasswordAuth from "../../components/authentication/EmailPasswordAuth";
// components
import Page from "../../components/Page";
// hooks
import { useNatively } from "hooks/useNatively";

// ----------------------------------------------------------------------

const RootStyle = styled(Page)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
    theme.palette.primary.light,
    0.05
  )} 100%)`,
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2)
  }
}));

const LoginCard = styled(Paper)(({ theme }) => ({
  maxWidth: 480,
  width: "100%",
  padding: theme.spacing(6),
  borderRadius: theme.spacing(2),
  boxShadow: `0 24px 48px ${alpha(theme.palette.grey[900], 0.12)}`,
  background: theme.palette.background.paper,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(4, 3),
    borderRadius: theme.spacing(1.5),
    maxWidth: "calc(100vw - 32px)",
    margin: "0 auto"
  },
  [theme.breakpoints.down("xs")]: {
    padding: theme.spacing(3, 2),
    maxWidth: "calc(100vw - 24px)"
  }
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(3)
  }
}));

const WelcomeContainer = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(4)
}));

const IllustrationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  "& img": {
    maxWidth: "100%",
    height: "auto",
    maxHeight: 240,
    opacity: 0.8
  }
}));

const DebugInfo = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: alpha(theme.palette.grey[500], 0.08),
  borderRadius: theme.spacing(1),
  textAlign: "center"
}));

// ----------------------------------------------------------------------

export default function Login() {
  const { t } = useTranslation();
  const { isNativeApp } = useNatively();

  return (
    <RootStyle title={t("auth.login.title")}>
      <LoginCard elevation={0}>
        <LogoContainer>
          <img
            src="/static/_haim/webot.png"
            alt="Webot"
            style={{
              height: 18,
              width: "auto"
            }}
          />
        </LogoContainer>

        <WelcomeContainer>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}>
            {t("auth.login.welcome")}
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {t("auth.login.subtitle")}
          </Typography>
        </WelcomeContainer>

        <AuthFirebaseSocials />

        <EmailPasswordAuth />

        <IllustrationContainer>
          <img alt="login" src="/static/illustrations/illustration_login.png" />
        </IllustrationContainer>

        {isNativeApp && (
          <DebugInfo>
            <Typography variant="caption" color="textSecondary" display="block">
              Host: {window.location.host}
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              Auth domain: {process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "N/A"}
            </Typography>
          </DebugInfo>
        )}
      </LoginCard>
    </RootStyle>
  );
}
